import React, { useEffect, useState } from 'react';
import { Wrench, TrendingUp, Info, Plus, Trash2, ExternalLink, AlertTriangle, CalendarClock, Pencil, Archive, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const SERVICE_TYPES = [
  { value: 'oil_change', label: 'Oil Change' },
  { value: 'tyre', label: 'Tyres' },
  { value: 'brake', label: 'Brakes' },
  { value: 'general', label: 'General Service' },
  { value: 'breakdown', label: 'Breakdown / Repair' },
  { value: 'insurance', label: 'Insurance / Registration' },
  { value: 'other', label: 'Other' },
];

const money = (n, cur = 'SAR') =>
  `${cur} ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const remainingContractTime = (endDateStr) => {
  if (!endDateStr) return null;
  const end = new Date(endDateStr);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const totalDays = Math.round((end - today) / (1000 * 60 * 60 * 24));
  const abs = Math.abs(totalDays);
  const months = Math.floor(abs / 30);
  const days = abs - months * 30;
  return {
    totalDays,
    isExpired: totalDays < 0,
    label: `${months} month${months !== 1 ? 's' : ''} ${days} day${days !== 1 ? 's' : ''}`,
  };
};

const emptyService = {
  service_date: new Date().toISOString().slice(0, 10),
  service_type: 'general',
  location: '',
  vendor: '',
  odometer_km: '',
  amount: '',
  currency: 'SAR',
  notes: '',
  invoice_number: '',
};

export const VehicleDetailDialog = ({ asset, open, onOpenChange, onAssetChanged }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState('info');
  const [services, setServices] = useState([]);
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [serviceForm, setServiceForm] = useState(emptyService);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    if (!asset) return;
    setLoading(true);
    try {
      const [svc, fin] = await Promise.all([
        api.get(`/fleet/${asset.id}/services`),
        api.get(`/fleet/${asset.id}/financials`),
      ]);
      setServices(svc.data || []);
      setFinancials(fin.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load vehicle details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && asset) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, asset?.id]);

  const handleAddService = async () => {
    if (!serviceForm.amount || Number(serviceForm.amount) <= 0) {
      toast.error('Amount is required');
      return;
    }
    try {
      await api.post(`/fleet/${asset.id}/services`, {
        ...serviceForm,
        amount: Number(serviceForm.amount),
        odometer_km: serviceForm.odometer_km ? Number(serviceForm.odometer_km) : null,
      });
      toast.success('Service record added');
      setShowAddService(false);
      setServiceForm(emptyService);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Delete this service record?')) return;
    try {
      await api.delete(`/fleet/services/${id}`);
      toast.success('Deleted');
      refresh();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const openEdit = () => {
    setEditForm({
      make: asset.make || '',
      model: asset.model || '',
      year: asset.year || '',
      registration_number: asset.registration_number || '',
      chassis_number: asset.chassis_number || '',
      asset_type: asset.asset_type || '',
      purchase_value: asset.purchase_value || '',
      purchase_date: asset.purchase_date || '',
      rental_rate_daily: asset.rental_rate_daily || '',
      rental_rate_monthly: asset.rental_rate_monthly || '',
      insurance_expiry: asset.insurance_expiry || '',
      registration_expiry: asset.registration_expiry || '',
      contract_start_date: asset.contract_start_date || '',
      contract_duration_years: asset.contract_duration_years || '',
      contract_notes: asset.contract_notes || '',
      supplier_name: asset.supplier_name || '',
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        if (['year', 'purchase_value', 'rental_rate_daily', 'rental_rate_monthly', 'contract_duration_years'].includes(k)) {
          payload[k] = Number(v);
        } else {
          payload[k] = v;
        }
      });
      await api.patch(`/assets/${asset.id}`, payload);
      toast.success('Vehicle updated');
      setShowEdit(false);
      onAssetChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm(`Archive ${asset.registration_number}? It will be removed from the active list. History and earnings stay intact.`)) return;
    try {
      await api.post(`/assets/${asset.id}/archive`);
      toast.success('Vehicle archived');
      onAssetChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive');
    }
  };

  const handleUnarchive = async () => {
    try {
      await api.post(`/assets/${asset.id}/unarchive`);
      toast.success('Vehicle restored');
      onAssetChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`⚠️ Permanently delete ${asset.registration_number}? This cannot be undone. (If the vehicle has any history, use Archive instead.)`)) return;
    try {
      await api.delete(`/assets/${asset.id}`);
      toast.success('Vehicle deleted');
      onAssetChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  if (!asset) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="vehicle-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span>{asset.make} {asset.model}</span>
            <span className="font-mono text-sm bg-slate-100 px-2 py-0.5 rounded">
              {asset.registration_number}
            </span>
            <Badge>{asset.status}</Badge>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="info" data-testid="tab-info">
              <Info className="h-4 w-4 mr-1" /> Info
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">
              <Wrench className="h-4 w-4 mr-1" /> Maintenance
            </TabsTrigger>
            <TabsTrigger value="financials" data-testid="tab-financials">
              <TrendingUp className="h-4 w-4 mr-1" /> Earnings
            </TabsTrigger>
          </TabsList>

          {/* Info tab */}
          <TabsContent value="info" className="space-y-3 pt-3">
            <InfoRow label="Type" value={asset.asset_type} />
            <InfoRow label="Year" value={asset.year} />
            <InfoRow label="Chassis" value={asset.chassis_number || '—'} />
            <InfoRow label="Ownership" value={(asset.ownership_type || '').replace('_', ' ')} />
            {asset.supplier_name && <InfoRow label="Supplier" value={asset.supplier_name} />}
            <InfoRow label="Purchase Value" value={money(asset.purchase_value)} />
            <InfoRow label="Purchase Date" value={asset.purchase_date} />
            {asset.rental_rate_daily && <InfoRow label="Daily Rate" value={money(asset.rental_rate_daily)} />}
            {asset.rental_rate_monthly && <InfoRow label="Monthly Rate" value={money(asset.rental_rate_monthly)} />}
            {asset.ownership_type === 'supplier_rented' && (asset.contract_start_date || asset.contract_end_date) && (
              <ContractPanel asset={asset} />
            )}
            <InfoRow label="Insurance Expiry" value={asset.insurance_expiry || '—'} />
            <InfoRow label="Registration Expiry" value={asset.registration_expiry || '—'} />
            {asset.current_deployment_id && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm">
                Currently mobilised on DN <span className="font-mono">{asset.current_deployment_id}</span>
                {asset.current_client_name && <> for <strong>{asset.current_client_name}</strong></>}
              </div>
            )}
          </TabsContent>

          {/* Maintenance tab */}
          <TabsContent value="services" className="space-y-3 pt-3">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                {services.length} record{services.length !== 1 ? 's' : ''} · Total spend:{' '}
                <span className="font-semibold text-slate-900">
                  {money(financials?.maintenance_spend || 0)}
                </span>
              </p>
              <Button size="sm" onClick={() => setShowAddService(!showAddService)} data-testid="add-service-button">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>

            {showAddService && (
              <Card className="p-3 space-y-3 bg-slate-50">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={serviceForm.service_date}
                      onChange={(e) => setServiceForm({ ...serviceForm, service_date: e.target.value })}
                      data-testid="service-date-input"
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={serviceForm.service_type} onValueChange={(v) => setServiceForm({ ...serviceForm, service_type: v })}>
                      <SelectTrigger data-testid="service-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SERVICE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount (SAR)</Label>
                    <Input
                      type="number"
                      value={serviceForm.amount}
                      onChange={(e) => setServiceForm({ ...serviceForm, amount: e.target.value })}
                      data-testid="service-amount-input"
                    />
                  </div>
                  <div>
                    <Label>Odometer (km)</Label>
                    <Input
                      type="number"
                      value={serviceForm.odometer_km}
                      onChange={(e) => setServiceForm({ ...serviceForm, odometer_km: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={serviceForm.location}
                      onChange={(e) => setServiceForm({ ...serviceForm, location: e.target.value })}
                      placeholder="e.g. Riyadh workshop"
                    />
                  </div>
                  <div>
                    <Label>Vendor</Label>
                    <Input
                      value={serviceForm.vendor}
                      onChange={(e) => setServiceForm({ ...serviceForm, vendor: e.target.value })}
                      placeholder="Vendor / garage"
                    />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    rows={2}
                    value={serviceForm.notes}
                    onChange={(e) => setServiceForm({ ...serviceForm, notes: e.target.value })}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowAddService(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleAddService} data-testid="submit-service-button">Save</Button>
                </div>
              </Card>
            )}

            <div className="divide-y border rounded-lg">
              {loading ? (
                <p className="p-4 text-center text-muted-foreground text-sm">Loading...</p>
              ) : services.length === 0 ? (
                <p className="p-4 text-center text-muted-foreground text-sm">No service records yet.</p>
              ) : (
                services.map((s) => (
                  <div key={s.id} className="p-3 flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{money(s.amount, s.currency)}</span>
                        <Badge variant="outline">{s.service_type}</Badge>
                        <span className="text-xs text-muted-foreground">{s.service_date}</span>
                      </div>
                      {s.location && <p className="text-sm mt-1">{s.location}{s.vendor ? ` · ${s.vendor}` : ''}</p>}
                      {s.notes && <p className="text-xs text-muted-foreground mt-1">{s.notes}</p>}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteService(s.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Financials tab */}
          <TabsContent value="financials" className="space-y-3 pt-3">
            {loading || !financials ? (
              <p className="text-muted-foreground text-sm">Loading...</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <StatCard
                    label="Total Earned"
                    value={money(financials.total_earned)}
                    sub={`${financials.deployment_count} deployment${financials.deployment_count !== 1 ? 's' : ''}`}
                    tone="emerald"
                  />
                  <StatCard
                    label="Total Spent"
                    value={money(financials.total_spent)}
                    sub={`Purchase + ${financials.service_count} services`}
                    tone="red"
                  />
                  <StatCard
                    label="Lifetime Profit"
                    value={money(financials.lifetime_profit)}
                    sub={financials.lifetime_profit >= 0 ? 'In the green' : 'Not yet break-even'}
                    tone={financials.lifetime_profit >= 0 ? 'emerald' : 'red'}
                  />
                </div>
                <div className="p-3 rounded-lg bg-slate-50 text-sm">
                  <p><span className="text-muted-foreground">Purchase value:</span> {money(financials.purchase_value)}</p>
                  <p><span className="text-muted-foreground">Maintenance spend:</span> {money(financials.maintenance_spend)}</p>
                  <p className="mt-1"><span className="text-muted-foreground">Operating profit (excludes purchase):</span> <strong>{money(financials.operating_profit)}</strong></p>
                </div>

                <div>
                  <p className="font-semibold text-sm mb-2">Deployments</p>
                  <div className="divide-y border rounded-lg">
                    {financials.deployments.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground text-center">No deployments yet.</p>
                    ) : (
                      financials.deployments.map((d) => (
                        <div key={d.dn_id} className="p-3 flex justify-between items-center">
                          <div>
                            <p className="font-semibold text-sm">{d.dn_number}</p>
                            <p className="text-xs text-muted-foreground">{d.client_name} · {d.delivery_date}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-sm">{money(d.attributed_amount, d.currency)}</p>
                            <Badge variant="outline" className="text-xs">{d.status}</Badge>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {isAdmin && asset.status !== 'archived' && (
              <>
                <Button size="sm" variant="outline" onClick={openEdit} data-testid="vehicle-edit-button">
                  <Pencil className="h-4 w-4 mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleArchive}
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  data-testid="vehicle-archive-button"
                >
                  <Archive className="h-4 w-4 mr-1" /> Archive
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDelete}
                  className="text-red-700 border-red-300 hover:bg-red-50"
                  data-testid="vehicle-delete-button"
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              </>
            )}
            {isAdmin && asset.status === 'archived' && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleUnarchive}
                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                data-testid="vehicle-unarchive-button"
              >
                <RotateCcw className="h-4 w-4 mr-1" /> Restore from Archive
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>

      {/* Edit modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="vehicle-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Vehicle · {asset.registration_number}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div><Label>Make</Label><Input value={editForm.make || ''} onChange={(e) => setEditForm({ ...editForm, make: e.target.value })} data-testid="edit-make-input" /></div>
            <div><Label>Model</Label><Input value={editForm.model || ''} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} data-testid="edit-model-input" /></div>
            <div><Label>Year</Label><Input type="number" value={editForm.year || ''} onChange={(e) => setEditForm({ ...editForm, year: e.target.value })} /></div>
            <div><Label>Registration No.</Label><Input value={editForm.registration_number || ''} onChange={(e) => setEditForm({ ...editForm, registration_number: e.target.value })} data-testid="edit-reg-input" /></div>
            <div><Label>Chassis No.</Label><Input value={editForm.chassis_number || ''} onChange={(e) => setEditForm({ ...editForm, chassis_number: e.target.value })} /></div>
            <div><Label>Asset Type</Label><Input value={editForm.asset_type || ''} onChange={(e) => setEditForm({ ...editForm, asset_type: e.target.value })} /></div>
            <div><Label>Purchase Value (SAR)</Label><Input type="number" value={editForm.purchase_value || ''} onChange={(e) => setEditForm({ ...editForm, purchase_value: e.target.value })} /></div>
            <div><Label>Purchase Date</Label><Input type="date" value={editForm.purchase_date || ''} onChange={(e) => setEditForm({ ...editForm, purchase_date: e.target.value })} /></div>
            {asset.ownership_type === 'supplier_rented' && (
              <>
                <div><Label>Supplier Name</Label><Input value={editForm.supplier_name || ''} onChange={(e) => setEditForm({ ...editForm, supplier_name: e.target.value })} /></div>
                <div><Label>Daily Rate</Label><Input type="number" value={editForm.rental_rate_daily || ''} onChange={(e) => setEditForm({ ...editForm, rental_rate_daily: e.target.value })} /></div>
                <div><Label>Monthly Rate</Label><Input type="number" value={editForm.rental_rate_monthly || ''} onChange={(e) => setEditForm({ ...editForm, rental_rate_monthly: e.target.value })} /></div>
                <div><Label>Contract Start</Label><Input type="date" value={editForm.contract_start_date || ''} onChange={(e) => setEditForm({ ...editForm, contract_start_date: e.target.value })} /></div>
                <div><Label>Contract Duration (yrs)</Label><Input type="number" step="0.25" value={editForm.contract_duration_years || ''} onChange={(e) => setEditForm({ ...editForm, contract_duration_years: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Contract Notes</Label><Input value={editForm.contract_notes || ''} onChange={(e) => setEditForm({ ...editForm, contract_notes: e.target.value })} /></div>
              </>
            )}
            <div><Label>Insurance Expiry</Label><Input type="date" value={editForm.insurance_expiry || ''} onChange={(e) => setEditForm({ ...editForm, insurance_expiry: e.target.value })} /></div>
            <div><Label>Registration Expiry</Label><Input type="date" value={editForm.registration_expiry || ''} onChange={(e) => setEditForm({ ...editForm, registration_expiry: e.target.value })} /></div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="edit-save-button">
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between text-sm border-b py-1">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

const ContractPanel = ({ asset }) => {
  const remaining = remainingContractTime(asset.contract_end_date);
  const tone = !remaining
    ? 'slate'
    : remaining.isExpired
      ? 'red'
      : remaining.totalDays <= 30
        ? 'orange'
        : remaining.totalDays <= 60
          ? 'amber'
          : 'emerald';
  const toneClasses = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    red: 'bg-red-50 border-red-300 text-red-900',
    orange: 'bg-orange-50 border-orange-300 text-orange-900',
    amber: 'bg-amber-50 border-amber-300 text-amber-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  }[tone];
  const showAlert = tone === 'red' || tone === 'orange';
  return (
    <div
      className={`mt-3 p-3 rounded-lg border ${toneClasses}`}
      data-testid="contract-panel"
    >
      <div className="flex items-center gap-2 font-semibold text-sm mb-2">
        <CalendarClock className="h-4 w-4" />
        Rental Contract
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="opacity-70">Start:</span>{' '}
          <span className="font-medium">{asset.contract_start_date || '—'}</span>
        </div>
        <div>
          <span className="opacity-70">End:</span>{' '}
          <span className="font-medium" data-testid="contract-end-date">{asset.contract_end_date || '—'}</span>
        </div>
        <div>
          <span className="opacity-70">Duration:</span>{' '}
          <span className="font-medium">
            {asset.contract_duration_years ? `${asset.contract_duration_years} year(s)` : '—'}
          </span>
        </div>
        <div>
          <span className="opacity-70">Remaining:</span>{' '}
          <span className="font-semibold" data-testid="contract-remaining">
            {remaining
              ? remaining.isExpired
                ? `Expired ${remaining.label} ago`
                : remaining.label
              : '—'}
          </span>
        </div>
      </div>
      {asset.contract_notes && (
        <p className="text-xs opacity-80 mt-2">{asset.contract_notes}</p>
      )}
      {showAlert && (
        <div
          className={`mt-2 flex items-center gap-2 text-xs font-semibold px-2 py-1.5 rounded ${
            tone === 'red' ? 'bg-red-100' : 'bg-orange-100'
          }`}
          data-testid="contract-alert"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          {tone === 'red'
            ? 'This contract has expired. Renew or off-hire the vehicle.'
            : 'Contract expiring within 30 days — begin renewal / return process.'}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, sub, tone }) => {
  const bg = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    slate: 'bg-slate-50 border-slate-200',
  }[tone] || 'bg-slate-50 border-slate-200';
  return (
    <div className={`p-3 rounded-lg border ${bg}`}>
      <p className="text-xs opacity-70">{label}</p>
      <p className="text-lg font-bold mt-1">{value}</p>
      {sub && <p className="text-xs opacity-70 mt-1">{sub}</p>}
    </div>
  );
};

export default VehicleDetailDialog;
